import type {
  ExecutiveCostTotals, ExecutiveDashboardResponse, ExecutivePeriod, ExecutiveQuery, ExecutiveTrendPoint, PayrollStatusCount, UnavailableItem, Variance,
} from './types';

/**
 * Synthetic Demo Data for the executive dashboard, used only by the mock gateway (`VITE_USE_MOCKS=true`) and the automated tests that render it. It is a
 * deterministic builder: the same query always gives the same answer, nothing is random, and nothing in a real backend response ever comes from here.
 * Twenty-four months of semi-monthly approved runs for a small company whose headcount grows from four to six, so every period choice, the comparison
 * and the twelve-month trend have something real-shaped to show. Money is built in whole cents so the sums are exact, and the identities the real API
 * guarantees hold here too (gross = net + employee taxes + employee deductions; total = gross + employer taxes).
 */
export const SYNTHETIC_DEMO_LABEL = 'Synthetic Demo Data';

/** The fixed "today" the fixture reports against (the mock summary's period is October 2026). */
export const SYNTHETIC_TODAY = '2026-10-31';

interface SyntheticRun {
  checkDate: string;
  grossCents: number;
  employeeTaxCents: number;
  employerTaxCents: number;
  deductionCents: number;
  netCents: number;
  employeeIds: string[];
}

const pad = (n: number) => String(n).padStart(2, '0');

function buildRuns(): SyntheticRun[] {
  const runs: SyntheticRun[] = [];
  for (let i = 0; i < 24; i += 1) {
    const monthIndex = 10 + i; // November 2024 is month index 10 of 2024
    const year = 2024 + Math.floor(monthIndex / 12);
    const month = (monthIndex % 12) + 1;
    const headcount = i < 12 ? 4 : i < 18 ? 5 : 6;
    const ids = Array.from({ length: headcount }, (_, n) => `synthetic-employee-${n + 1}`);
    const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
    for (const [half, day] of [[0, 15], [1, last]] as const) {
      const grossCents = 350000 + i * 9000 + half * 4000 + (i % 3) * 12500 + headcount * 30000;
      const employeeTaxCents = Math.round(grossCents * 0.19);
      const deductionCents = Math.round(grossCents * 0.06);
      runs.push({
        checkDate: `${year}-${pad(month)}-${pad(day)}`,
        grossCents,
        employeeTaxCents,
        employerTaxCents: Math.round(grossCents * 0.0825),
        deductionCents,
        netCents: grossCents - employeeTaxCents - deductionCents,
        employeeIds: ids,
      });
    }
  }
  return runs;
}

const RUNS = buildRuns();

const toDate = (iso: string) => new Date(`${iso}T00:00:00Z`);
const iso = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
const monthStart = (d: Date, offset = 0) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + offset, 1));
const monthEnd = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * 86_400_000);
const daysBetween = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 86_400_000) + 1;

/** The same rules as `ExecutivePeriodResolver` on the server. */
export function resolveSyntheticPeriod(query: ExecutiveQuery): ExecutivePeriod {
  const today = toDate(SYNTHETIC_TODAY);
  const make = (label: string, s: Date, e: Date, cLabel: string, cs: Date, ce: Date): ExecutivePeriod => ({
    kind: query.period, label, start: iso(s), end: iso(e), comparisonLabel: cLabel, comparisonStart: iso(cs), comparisonEnd: iso(ce), timeZoneId: 'UTC',
  });
  switch (query.period) {
    case 'lastMonth': {
      const s = monthStart(today, -1);
      return make('Last Month', s, monthEnd(s), 'The Month Before', monthStart(s, -1), monthEnd(monthStart(s, -1)));
    }
    case 'thisQuarter': {
      const s = new Date(Date.UTC(today.getUTCFullYear(), Math.floor(today.getUTCMonth() / 3) * 3, 1));
      const ps = monthStart(s, -3);
      return make('This Quarter', s, monthEnd(monthStart(s, 2)), 'Last Quarter', ps, monthEnd(monthStart(ps, 2)));
    }
    case 'yearToDate': {
      const s = new Date(Date.UTC(today.getUTCFullYear(), 0, 1));
      return make('Year to Date', s, today, 'Same Dates Last Year', new Date(Date.UTC(today.getUTCFullYear() - 1, 0, 1)), new Date(Date.UTC(today.getUTCFullYear() - 1, today.getUTCMonth(), today.getUTCDate())));
    }
    case 'trailing12Months': {
      const e = monthEnd(today);
      const s = monthStart(e, -11);
      return make('Last 12 Months', s, e, 'The 12 Months Before', monthStart(s, -12), addDays(s, -1));
    }
    case 'custom': {
      const s = toDate(query.from ?? SYNTHETIC_TODAY);
      const e = toDate(query.to ?? SYNTHETIC_TODAY);
      const length = daysBetween(s, e);
      const ce = addDays(s, -1);
      return make('Custom Range', s, e, 'The Period Before', addDays(ce, 1 - length), ce);
    }
    default: {
      const s = monthStart(today);
      return make('This Month', s, monthEnd(today), 'Last Month', monthStart(s, -1), monthEnd(monthStart(s, -1)));
    }
  }
}

const inRange = (r: SyntheticRun, start: string, end: string) => r.checkDate >= start && r.checkDate <= end;
const dollars = (cents: number) => cents / 100;

function totals(runs: SyntheticRun[]): ExecutiveCostTotals {
  const sum = (pick: (r: SyntheticRun) => number) => runs.reduce((s, r) => s + pick(r), 0);
  const grossCents = sum((r) => r.grossCents);
  const employerCents = sum((r) => r.employerTaxCents);
  const employeesPaid = new Set(runs.flatMap((r) => r.employeeIds)).size;
  const totalCents = grossCents + employerCents;
  return {
    grossWages: dollars(grossCents),
    employerTaxes: dollars(employerCents),
    employeeTaxes: dollars(sum((r) => r.employeeTaxCents)),
    employeeDeductions: dollars(sum((r) => r.deductionCents)),
    netPay: dollars(sum((r) => r.netCents)),
    totalEmployerCost: dollars(totalCents),
    employeesPaid,
    averageEmployerCostPerPaidEmployee: employeesPaid === 0 ? null : Math.round(totalCents / employeesPaid) / 100,
    reconciliationDifference: dollars(grossCents - sum((r) => r.netCents) - sum((r) => r.employeeTaxCents) - sum((r) => r.deductionCents)),
  };
}

function variance(current: number, comparison: number): Variance {
  const amount = Math.round((current - comparison) * 100) / 100;
  return { amount, percent: comparison === 0 ? null : Math.round((amount / comparison) * 1000) / 10, isIncrease: amount >= 0 };
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const UNAVAILABLE: UnavailableItem[] = [
  { id: 'cost-by-group', label: 'Cost by department, location, work state, worker category and pay schedule',
    reason: "EPN stores these as the employee's current values, not as they were on the check date, so a historical split would be wrong. They need to be recorded when a payroll is approved." },
  { id: 'employee-filters', label: 'Filters for department, location, work state, employee type and pay schedule',
    reason: 'The same values are needed to filter payroll cost, and they are not recorded at the check date yet.' },
  { id: 'active-employees', label: 'Active employees, full-time and part-time counts',
    reason: 'This needs the Employees service, which the dashboard does not read yet.' },
  { id: 'deposit-deadlines', label: 'Tax deposit deadlines',
    reason: "A deposit deadline depends on the company's deposit schedule, which the dashboard cannot read yet. The return filing dates shown are a different date." },
  { id: 'run-states', label: 'Funding required, scheduled, processing, completed, failed and reversed payrolls',
    reason: 'Payroll Review records Draft, Awaiting approval, Approved, Rejected and Withdrawn. The later states would have to be derived from payment records, which the dashboard does not read yet.' },
  { id: 'cost-drivers', label: 'Why payroll cost changed (new hires, terminations, rate changes, hours, overtime, bonuses)',
    reason: 'Payroll Calculation does not record these components per run, so a driver breakdown would be a guess.' },
];

export function buildSyntheticExecutive(query: ExecutiveQuery): ExecutiveDashboardResponse {
  const period = resolveSyntheticPeriod(query);
  const current = totals(RUNS.filter((r) => inRange(r, period.start, period.end)));
  const comparison = totals(RUNS.filter((r) => inRange(r, period.comparisonStart, period.comparisonEnd)));

  const endMonth = monthStart(toDate(period.end));
  const trend: ExecutiveTrendPoint[] = Array.from({ length: 12 }, (_, n) => {
    const start = monthStart(endMonth, n - 11);
    const t = totals(RUNS.filter((r) => inRange(r, iso(start), iso(monthEnd(start)))));
    return {
      monthStart: iso(start), label: `${MONTH_NAMES[start.getUTCMonth()]} ${start.getUTCFullYear()}`,
      grossWages: t.grossWages, employerTaxes: t.employerTaxes, totalEmployerCost: t.totalEmployerCost, employeesPaid: t.employeesPaid,
    };
  });

  const payrollStatuses: PayrollStatusCount[] = [
    { status: 'Draft', label: 'Draft', count: 1, actionPath: '/payroll-review' },
    { status: 'PendingApproval', label: 'Awaiting approval', count: 1, actionPath: '/payroll-review' },
    { status: 'Rejected', label: 'Rejected', count: 0, actionPath: '/payroll-review' },
    { status: 'Approved', label: 'Approved in this period', count: RUNS.filter((r) => inRange(r, period.start, period.end)).length, actionPath: '/payroll-review' },
  ];

  return {
    generatedAtUtc: '2026-10-31T21:14:00Z',
    period,
    current,
    comparison,
    totalCostVariance: variance(current.totalEmployerCost, comparison.totalEmployerCost),
    grossWagesVariance: variance(current.grossWages, comparison.grossWages),
    trend,
    payrollStatuses,
    obligations: [
      { kind: 'ReturnFiling', title: 'Form 941 return, Q3 2026', date: '2026-11-02',
        note: 'Return filing date, moved from Oct 31, 2026 for a weekend or holiday. Not a deposit deadline.', actionPath: '/tax-filings' },
      { kind: 'ReturnFiling', title: 'Form 940 return, 2026', date: '2027-02-01',
        note: 'Return filing date, moved from Jan 31, 2027 for a weekend or holiday. Not a deposit deadline.', actionPath: '/tax-filings' },
      { kind: 'ReturnFiling', title: 'Form 941 return, Q4 2026', date: '2027-02-01',
        note: 'Return filing date, moved from Jan 31, 2027 for a weekend or holiday. Not a deposit deadline.', actionPath: '/tax-filings' },
    ],
    alerts: [
      { severity: 'critical', title: 'Form 941 amount unpaid after its due date',
        detail: '$1,204.50 was due Jul 31, 2026 and no deposit has settled.', actionPath: '/payroll-taxes' },
      { severity: 'attention', title: 'Payroll waiting for approval', detail: '1 payroll run is waiting for an approver.', actionPath: '/payroll-review' },
    ],
    unavailable: UNAVAILABLE,
    availability: { payrollUnavailable: false, runsUnavailable: false, obligationsUnavailable: false, taxesUnavailable: false },
  };
}
